/** Shared beneficiary (Contact) form shape + helpers, used by the edit page
 * and both create modals via <BeneficiaryFormFields>. Kept in a plain module
 * (not the .tsx) so the component file only exports a component. */
import type { BusinessType, Contact } from "@/lib/types";

export type BeneficiaryForm = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_line: string;
  postal_code: string;
  city: string;
  country: string;
  is_business: boolean;
  business_type: BusinessType;
  legal_name: string;
  registration_number: string;
  vat_id: string;
  company_contact_name: string;
  company_contact_email: string;
  company_contact_phone: string;
};

export const emptyBeneficiaryForm: BeneficiaryForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address_line: "",
  postal_code: "",
  city: "",
  country: "",
  is_business: false,
  business_type: "",
  legal_name: "",
  registration_number: "",
  vat_id: "",
  company_contact_name: "",
  company_contact_email: "",
  company_contact_phone: "",
};

/** Seed a form from an existing Contact (edit path). */
export function beneficiaryFormFromContact(c: Contact): BeneficiaryForm {
  return {
    first_name: c.first_name ?? "",
    last_name: c.last_name ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    address_line: c.address_line ?? "",
    postal_code: c.postal_code ?? "",
    city: c.city ?? "",
    country: c.country ?? "",
    is_business: !!c.is_business,
    business_type: (c.business_type ?? "") as BusinessType,
    legal_name: c.legal_name ?? "",
    registration_number: c.registration_number ?? "",
    vat_id: c.vat_id ?? "",
    company_contact_name: c.company_contact_name ?? "",
    company_contact_email: c.company_contact_email ?? "",
    company_contact_phone: c.company_contact_phone ?? "",
  };
}

/** Build the API payload, gating the legal-entity fields on `is_business` (so
 * an unticked entity never persists stale company data).
 *
 * `includeAddress` (default true) controls the flat address columns. On the
 * edit page addresses are managed via the structured <ContactAddresses>
 * manager (the source of truth — the flat columns are kept in sync by the
 * backend structured→flat mirror), so the edit form omits them to avoid
 * writing stale flat values back over the mirror. */
export function beneficiaryPayload(
  form: BeneficiaryForm,
  customFields: Record<string, string>,
  opts: { includeAddress?: boolean } = {},
): Record<string, unknown> {
  const { includeAddress = true } = opts;
  return {
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    ...(includeAddress
      ? {
          address_line: form.address_line.trim(),
          postal_code: form.postal_code.trim(),
          city: form.city.trim(),
          country: form.country,
        }
      : {}),
    is_business: form.is_business,
    business_type: form.is_business ? form.business_type : "",
    legal_name: form.is_business ? form.legal_name.trim() : "",
    registration_number: form.is_business ? form.registration_number.trim() : "",
    vat_id: form.is_business ? form.vat_id.trim() : "",
    company_contact_name: form.is_business ? form.company_contact_name.trim() : "",
    company_contact_email: form.is_business ? form.company_contact_email.trim() : "",
    company_contact_phone: form.is_business ? form.company_contact_phone.trim() : "",
    custom_fields_jsonb: customFields,
  };
}

/** Map the shared beneficiary form onto the session/invite participant wire
 * shape (identity + company + flat address). `country` becomes `address_country`
 * (the invite/session column name); legal-entity fields gate on `is_business`.
 * The backend upserts a `session_participant` Contact from these (Phase 3). */
export function beneficiaryFormToSessionParticipant(form: BeneficiaryForm) {
  const biz = form.is_business;
  return {
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    address_line: form.address_line.trim(),
    city: form.city.trim(),
    postal_code: form.postal_code.trim(),
    address_country: (form.country || "").trim().toUpperCase(),
    is_business: biz,
    legal_name: biz ? form.legal_name.trim() : "",
    registration_number: biz ? form.registration_number.trim() : "",
    vat_id: biz ? form.vat_id.trim() : "",
    company_contact_name: biz ? form.company_contact_name.trim() : "",
    company_contact_email: biz ? form.company_contact_email.trim() : "",
    company_contact_phone: biz ? form.company_contact_phone.trim() : "",
  };
}

/** Identity: first + last name, OR (for a legal entity) a legal/company
 * name on its own — mirrors the backend's `_validate_identifiable` rule
 * (apps/campaigns/serializers.py) used on the import path, so a company
 * with no named contact person (common for CEE-style leads: only a
 * "raison sociale" + SIREN, never a person) isn't blocked from being
 * created/edited here just because Prénom/Nom are blank. Either way, at
 * least one of email / phone is still required to reach the beneficiary. */
export function canSubmitBeneficiary(form: BeneficiaryForm): boolean {
  const hasPersonName = !!(form.first_name.trim() && form.last_name.trim());
  const hasLegalName = form.is_business && !!form.legal_name.trim();
  return (
    (hasPersonName || hasLegalName) &&
    !!(form.email.trim() || form.phone.trim())
  );
}
